import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  Collapse
} from '@mui/material';
import {
  Tune as FilterIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon2
} from '@mui/icons-material';
import { AttributeFilters } from '../types';
import { getPersonColor, isAttributeSelectable } from '../utils/helpers';

interface AttributeFilterProps {
  attributeFilters: AttributeFilters;
  onFilterChange: (type: 'people' | 'location' | 'time', value: string, checked: boolean) => void;
  uniquePeople: Array<{ originalName: string; displayName: string }>;
  uniqueMapLocations: string[];
  uniqueLocalLocations: string[];
  uniqueDates: string[];
  attributeLock: any;
  getRelevantLocations: (attribute: string, value: string) => string[];
  sceneObjects: any[]; // Add this to get parent location relationships
  currentCaseId: string; // Add this to filter by current case
}

const AttributeFilter: React.FC<AttributeFilterProps> = ({
  attributeFilters,
  onFilterChange,
  uniquePeople,
  uniqueMapLocations,
  uniqueLocalLocations,
  uniqueDates,
  attributeLock,
  getRelevantLocations,
  sceneObjects,
  currentCaseId
}) => {

  const [expandedMapLocations, setExpandedMapLocations] = React.useState<Set<string>>(new Set());

  // Group people by type
  const groupedPeople = uniquePeople.reduce((acc, person) => {
    let type = 'Person of Interest';
    if (person.originalName.includes('Detective') || person.originalName.includes('Police') || person.originalName.includes('Forensic') || person.originalName.includes('Investigator') || person.originalName.includes('Sergeant') || person.originalName.includes('Officer')) type = 'Home Team';
    else if (person.originalName.includes('Victim')) type = 'Victim';
    else if (person.originalName.includes('Person of Interest')) type = 'Person of Interest';
    else if (person.originalName.includes('Witness')) type = 'Witness';
    else if (person.originalName.includes('Security Guard')) type = 'Security';
    
    if (!acc[type]) acc[type] = [];
    acc[type].push(person);
    return acc;
  }, {} as Record<string, Array<{ originalName: string; displayName: string }>>);

  // Group local locations by their parent map location
  const localLocationsByParent: Record<string, Set<string>> = sceneObjects
    .filter(obj => obj.location.mode === 'local' && obj.caseId === currentCaseId)
    .reduce((acc, obj) => {
      const parentLocation = obj.location.local?.parentLocation || 'Unknown';
      const localName = obj.location.local?.name || 'Unknown';
      if (!acc[parentLocation]) acc[parentLocation] = new Set();
      acc[parentLocation].add(localName);
      return acc;
    }, {} as Record<string, Set<string>>);

  const handleMapLocationToggle = (location: string) => {
    const newExpanded = new Set(expandedMapLocations);
    if (newExpanded.has(location)) {
      newExpanded.delete(location);
    } else {
      newExpanded.add(location);
    }
    setExpandedMapLocations(newExpanded);
  };

  return (
    <Box sx={{ mb: 1 }}>
              <Accordion defaultExpanded sx={{ boxShadow: 'none', border: 'none' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ 
            minHeight: 40, 
            backgroundColor: '#f8f9fa',
            borderBottom: 'none'
          }}>
            <Box display="flex" alignItems="flex-start">
              <FilterIcon sx={{ mr: 1, color: 'primary.main', fontSize: 18, mt: 0.5 }} />
              <Typography variant="subtitle2" sx={{ 
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#2c3e50'
              }}>
                Attribute Filter
              </Typography>
            </Box>
          </AccordionSummary>
                  <AccordionDetails sx={{ pt: 1, pb: 1 }}>

        {/* People Filter - Grouped by Type with Buttons */}
        <Box mb={0.5}>
          <Box display="flex" alignItems="center" mb={0.5}>
            <PersonIcon sx={{ mr: 1, color: 'primary.main', fontSize: 18 }} />
            <Typography variant="subtitle2" sx={{ fontSize: '0.875rem' }}>People</Typography>
            <Chip
              label={uniquePeople.length}
              size="small"
              sx={{ ml: 1, height: 20, fontSize: '0.75rem' }}
              color="primary"
              variant="outlined"
            />
          </Box>
          {Object.entries(groupedPeople).map(([type, people]) => (
            <Box key={type} mb={1}>
              <Box display="flex" alignItems="flex-start" mb={0.5}>
                                 <Box 
                   component="span"
                   sx={{ 
                     color: '#666',
                     fontWeight: 'bold',
                     fontSize: '0.75rem',
                     minWidth: '100px',
                     textAlign: 'left'
                   }}
                 >
                   {type} ({people.length})
                 </Box>
                <Box display="flex" flexWrap="wrap" gap={0.5} ml={1}>
                                     {people.map(person => {
                     const isSelectable = isAttributeSelectable('people', person.originalName, attributeLock, getRelevantLocations, sceneObjects, currentCaseId);
                     const isSelected = attributeFilters.people[person.originalName] !== false;
                     const personColor = getPersonColor(person.originalName, uniquePeople.map(p => p.originalName));
                    
                    return (
                      <Button
                        key={person.originalName}
                        variant={isSelected ? "outlined" : "text"}
                        size="small"
                        onClick={() => onFilterChange('people', person.originalName, !isSelected)}
                        disabled={!isSelectable}
                                                 sx={{
                           fontSize: '0.7rem',
                           padding: '2px 6px',
                           minWidth: 'auto',
                           height: '24px',
                           border: isSelected ? `1px solid ${personColor}` : '1px solid transparent',
                           color: isSelectable ? (isSelected ? 'white' : personColor) : '#999',
                           backgroundColor: isSelectable ? (isSelected ? personColor : 'white') : '#f5f5f5',
                           '&:hover': {
                             backgroundColor: isSelectable ? (isSelected ? personColor : 'grey.50') : '#f5f5f5',
                             border: isSelectable ? `1px solid ${personColor}` : '1px solid transparent'
                           },
                           '&:disabled': {
                             color: '#999',
                             backgroundColor: '#f5f5f5',
                             opacity: 0.5,
                             cursor: 'not-allowed'
                           },
                           opacity: isSelectable ? 1 : 0.5,
                           textTransform: 'none'
                         }}
                      >
                        {person.displayName}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Locations Filter - Combined Map and Local */}
        <Box mb={1}>
          <Box display="flex" alignItems="flex-start" mb={0.5}>
            <LocationIcon sx={{ mr: 1, color: 'primary.main', fontSize: 18, mt: 0.5 }} />
            <Typography variant="subtitle2" sx={{ fontSize: '0.875rem' }}>Locations</Typography>
            <Chip
              label={uniqueMapLocations.length + uniqueLocalLocations.length}
              size="small"
              sx={{ ml: 1, height: 20, fontSize: '0.75rem' }}
              color="primary"
              variant="outlined"
            />
          </Box>
          
          <Box sx={{ textAlign: 'left' }}>
            {/* Locations with sub-locations - each on new line */}
            {uniqueMapLocations.filter(location => localLocationsByParent[location]).map(location => {
              const isSelectable = isAttributeSelectable('location', location, attributeLock, getRelevantLocations, sceneObjects, currentCaseId);
              const isSelected = attributeFilters.location[location] !== false;
              const hasLocalLocations = localLocationsByParent[location];
              
              return (
                <Box key={location} sx={{ mb: 0.5, display: 'block' }}>
                  <Box display="flex" alignItems="center">
                    <Button
                      variant={isSelected ? "outlined" : "text"}
                      size="small"
                      onClick={() => onFilterChange('location', location, !isSelected)}
                      disabled={!isSelectable}
                      sx={{
                        fontSize: '0.7rem',
                        padding: '2px 6px',
                        minWidth: 'auto',
                        height: '24px',
                        border: isSelected ? '1px solid #1976d2' : '1px solid transparent',
                        color: isSelectable ? (isSelected ? 'white' : '#1976d2') : '#999',
                        backgroundColor: isSelectable ? (isSelected ? '#1976d2' : 'white') : '#f5f5f5',
                        '&:hover': {
                          backgroundColor: isSelectable ? (isSelected ? '#1976d2' : 'grey.50') : '#f5f5f5',
                          border: isSelectable ? '1px solid #1976d2' : '1px solid transparent'
                        },
                        '&:disabled': {
                          color: '#999',
                          backgroundColor: '#f5f5f5',
                          opacity: 0.5,
                          cursor: 'not-allowed'
                        },
                        opacity: isSelectable ? 1 : 0.5,
                        textTransform: 'none',
                        justifyContent: 'flex-start'
                      }}
                    >
                      {location}
                    </Button>
                    {hasLocalLocations && (
                      <Box 
                        display="flex" 
                        alignItems="center" 
                        sx={{ 
                          cursor: 'pointer',
                          ml: 1,
                          '&:hover': { backgroundColor: 'grey.50' },
                          borderRadius: 1,
                          px: 0.5,
                          py: 0.25
                        }}
                        onClick={() => handleMapLocationToggle(location)}
                      >
                        {expandedMapLocations.has(location) ? 
                          <ExpandLessIcon sx={{ fontSize: 14, mr: 0.5 }} /> : 
                          <ExpandMoreIcon2 sx={{ fontSize: 14, mr: 0.5 }} />
                        }
                        <Box 
                          component="span"
                          sx={{ 
                            color: '#666',
                            fontSize: '0.65rem'
                          }}
                        >
                          ({(() => {
                            const totalLocalLocations = hasLocalLocations.size;
                            const relevantLocalLocations = Array.from(hasLocalLocations).filter(localName => {
                              // Check if this local location is available based on locks
                              const isSelectableByLock = isAttributeSelectable('location', localName, attributeLock, getRelevantLocations, sceneObjects, currentCaseId);
                              
                              // Check if this local location is available based on current attribute filters
                              const isSelectableByFilters = attributeFilters.location[localName] !== false;
                              
                              // A local location is relevant if it's both selectable by locks AND not filtered out
                              return isSelectableByLock && isSelectableByFilters;
                            }).length;

                            return `${relevantLocalLocations}/${totalLocalLocations}`;
                          })()})
                        </Box>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Local Locations as sub-list */}
                  {hasLocalLocations && (
                    <Collapse in={expandedMapLocations.has(location)}>
                      <Box ml={2} mt={0.5}>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {Array.from(hasLocalLocations).map(localName => {
                            const isSelectable = isAttributeSelectable('location', localName, attributeLock, getRelevantLocations, sceneObjects, currentCaseId);
                            const isSelected = attributeFilters.location[localName] !== false;
                           
                           return (
                             <Button
                               key={localName}
                               variant={isSelected ? "outlined" : "text"}
                               size="small"
                               onClick={() => onFilterChange('location', localName, !isSelected)}
                               disabled={!isSelectable}
                               sx={{
                                 fontSize: '0.65rem',
                                 padding: '1px 4px',
                                 minWidth: 'auto',
                                 height: '20px',
                                 border: isSelected ? '1px solid #2e7d32' : '1px solid transparent',
                                 color: isSelectable ? (isSelected ? 'white' : '#2e7d32') : '#999',
                                 backgroundColor: isSelectable ? (isSelected ? '#2e7d32' : 'white') : '#f5f5f5',
                                 '&:hover': {
                                   backgroundColor: isSelectable ? (isSelected ? '#2e7d32' : 'grey.50') : '#f5f5f5',
                                   border: isSelectable ? '1px solid #2e7d32' : '1px solid transparent'
                                 },
                                 '&:disabled': {
                                   color: '#999',
                                   backgroundColor: '#f5f5f5',
                                   opacity: 0.5,
                                   cursor: 'not-allowed'
                                 },
                                 opacity: isSelectable ? 1 : 0.5,
                                 textTransform: 'none'
                               }}
                             >
                               {localName}
                             </Button>
                           );
                         })}
                        </Box>
                      </Box>
                    </Collapse>
                  )}
                </Box>
              );
            })}
            
            {/* Single locations - flow inline */}
            <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
              {uniqueMapLocations.filter(location => !localLocationsByParent[location]).map(location => {
                const isSelectable = isAttributeSelectable('location', location, attributeLock, getRelevantLocations, sceneObjects, currentCaseId);
                const isSelected = attributeFilters.location[location] !== false;
                
                return (
                  <Button
                    key={location}
                    variant={isSelected ? "outlined" : "text"}
                    size="small"
                    onClick={() => onFilterChange('location', location, !isSelected)}
                    disabled={!isSelectable}
                    sx={{
                      fontSize: '0.7rem',
                      padding: '2px 6px',
                      minWidth: 'auto',
                      height: '24px',
                      border: isSelected ? '1px solid #1976d2' : '1px solid transparent',
                      color: isSelectable ? (isSelected ? 'white' : '#1976d2') : '#999',
                      backgroundColor: isSelectable ? (isSelected ? '#1976d2' : 'white') : '#f5f5f5',
                      '&:hover': {
                        backgroundColor: isSelectable ? (isSelected ? '#1976d2' : 'grey.50') : '#f5f5f5',
                        border: isSelectable ? '1px solid #1976d2' : '1px solid transparent'
                      },
                      '&:disabled': {
                        color: '#999',
                        backgroundColor: '#f5f5f5',
                        opacity: 0.5,
                        cursor: 'not-allowed'
                      },
                      opacity: isSelectable ? 1 : 0.5,
                      textTransform: 'none',
                      justifyContent: 'flex-start'
                    }}
                  >
                    {location}
                  </Button>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* Time Filter */}
        <Box>
          <Box display="flex" alignItems="flex-start" mb={0.5}>
            <ScheduleIcon sx={{ mr: 1, color: 'primary.main', fontSize: 18, mt: 0.5 }} />
            <Typography variant="subtitle2" sx={{ fontSize: '0.875rem' }}>Time</Typography>
            <Chip
              label={uniqueDates.length}
              size="small"
              sx={{ ml: 1, height: 20, fontSize: '0.75rem' }}
              color="primary"
              variant="outlined"
            />
          </Box>
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {uniqueDates.map(date => {
              const isSelectable = isAttributeSelectable('time', date, attributeLock, getRelevantLocations, sceneObjects, currentCaseId);
              const isSelected = attributeFilters.time[date] !== false;
              
              return (
                <Button
                  key={date}
                  variant={isSelected ? "outlined" : "text"}
                  size="small"
                  onClick={() => onFilterChange('time', date, !isSelected)}
                  disabled={!isSelectable}
                  sx={{
                    fontSize: '0.7rem',
                    padding: '2px 6px',
                    minWidth: 'auto',
                    height: '24px',
                    border: isSelected ? '1px solid #1976d2' : '1px solid transparent',
                    color: isSelectable ? (isSelected ? 'white' : '#1976d2') : '#999',
                    backgroundColor: isSelectable ? (isSelected ? '#1976d2' : 'white') : '#f5f5f5',
                    '&:hover': {
                      backgroundColor: isSelectable ? (isSelected ? '#1976d2' : 'grey.50') : '#f5f5f5',
                      border: isSelectable ? '1px solid #1976d2' : '1px solid transparent'
                    },
                    '&:disabled': {
                      color: '#999',
                      backgroundColor: '#f5f5f5',
                      opacity: 0.5,
                      cursor: 'not-allowed'
                    },
                    opacity: isSelectable ? 1 : 0.5,
                    textTransform: 'none',
                    justifyContent: 'flex-start'
                  }}
                >
                  {date}
                </Button>
              );
            })}
          </Box>
        </Box>
          </AccordionDetails>
        </Accordion>
    </Box>
  );
};

export default AttributeFilter;
